import { SessionEventSummarySchema } from "@/domains/openapi/types";

// Shared font family for terminal-style design
export const TERMINAL_FONT_FAMILY =
  "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace";

export const SESSION_EVENT_KINDS = [
  { value: "USER_ACTION_V1", label: "User Action" },
  { value: "AGENT_PLANNING_V1", label: "Agent Planning" },
  { value: "AGENT_ACTION_V1", label: "Agent Action" },
  { value: "SYSTEM_NOTIFICATION_V1", label: "System Notification" },
] as const;

// Event type definitions for rendering strategy
export const EVENT_RENDERING_CONFIG = {
  USER_ACTION_V1: {
    icon: "▶",
    alignment: "right" as const,
    useEnriched: false,
  },
  AGENT_PLANNING_V1: {
    icon: "🧠",
    alignment: "left" as const,
    useEnriched: true,
  },
  AGENT_ACTION_V1: {
    icon: "⚙",
    alignment: "left" as const,
    useEnriched: true,
  },
  SYSTEM_NOTIFICATION_V1: {
    icon: "🔔",
    alignment: "left" as const,
    useEnriched: true,
  },
} as const;

export type EventKind = keyof typeof EVENT_RENDERING_CONFIG;

// Helper function to get event configuration
export const getEventConfig = (kind: string) => {
  return (
    EVENT_RENDERING_CONFIG[kind as EventKind] ?? {
      icon: "◦",
      alignment: "left" as const,
      useEnriched: false,
    }
  );
};

// Helper function to get event sub-kind for display
export const getEventSubKind = (event: SessionEventSummarySchema): string | null => {
  switch (event.data.kind) {
    case "AGENT_ACTION_V1":
      // Type guard to ensure we have the correct structure
      if (
        event.data.value &&
        typeof event.data.value === "object" &&
        "tool_call_kind" in event.data.value
      ) {
        return event.data.value.tool_call_kind as string;
      }
      return null;

    case "SYSTEM_NOTIFICATION_V1":
      // Type guard for system notifications
      if (
        event.data.value &&
        typeof event.data.value === "object" &&
        "notification_kind" in event.data.value
      ) {
        return event.data.value.notification_kind as string;
      }
      return null;

    default:
      return null;
  }
};

// Helper function to get display kind (sub-kind if available, otherwise main kind)
export const getDisplayKind = (event: SessionEventSummarySchema): string => {
  const subKind = getEventSubKind(event);
  const displayKind = subKind ?? event.data.kind;

  // Keep underscores as-is for technical display
  return displayKind;
};

export const getEventParts = (event: SessionEventSummarySchema) => {
  // Type guard to check if the value has parts
  const hasPartsInValue = (value: unknown): value is { parts: unknown[] } => {
    if (!value || typeof value !== "object" || value === null) {
      return false;
    }

    const obj = value as Record<string, unknown>;
    return "parts" in obj && Array.isArray(obj.parts);
  };

  if (hasPartsInValue(event.data.value)) {
    return event.data.value.parts;
  }

  return null;
};

// Session part kinds
export const SESSION_PART_KINDS = [
  "MARKDOWN_V1",
  "REASONING_V1",
  "WEB_CLIP_V1",
  "ARTIFACT_V1",
] as const;
